import { useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SiteContext } from "@/App";
import { BarChart2, Instagram, Settings, Trophy, Shield } from "lucide-react";
import LoginView from "@/components/custom/admin/LoginView";
import SettingsTab from "@/components/custom/admin/SettingsTab";
import PostsTab from "@/components/custom/admin/PostsTab";
import StatsTab from "@/components/custom/admin/StatsTab";
import AccoladesScanTab from "@/components/custom/admin/AccoladesScanTab";
import SecurityTab from "@/components/custom/admin/SecurityTab";

export default function AdminPanel({ open, onClose }) {
  const { setSettings, setAdminToken } = useContext(SiteContext);
  const [token, setToken] = useState(null);

  const handleLogin = (t) => {
    setToken(t);
    setAdminToken(t);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] bg-ink-950"
        >
          <div className="h-full overflow-y-auto">
            <div className="sticky top-0 z-10 bg-ink-950/95 backdrop-blur-sm border-b border-ink-600 px-6 md:px-12 py-4 flex items-center justify-between">
              <h1 className="font-cinzel text-lg tracking-widest text-[#E5E5E5]">
                {token ? "Site Management" : "Admin Access"}
              </h1>
              <button
                data-testid="admin-close"
                onClick={onClose}
                className="text-[#525252] hover:text-[#E5E5E5] transition-colors font-manrope text-xs tracking-widest uppercase border border-ink-600 px-4 py-2 rounded-sm hover:border-verde"
              >
                Close
              </button>
            </div>

            <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
              {!token ? (
                <div className="max-w-md mx-auto">
                  <LoginView onLogin={handleLogin} />
                </div>
              ) : (
                <Tabs defaultValue="settings">
                  <TabsList className="w-full bg-ink-800 border border-ink-600 flex-wrap h-auto gap-1 p-1 mb-6">
                    <TabsTrigger value="settings" className="flex-1 data-[state=active]:bg-verde/20 text-[#A3A3A3] data-[state=active]:text-verde text-sm py-2.5">
                      <Settings size={14} className="mr-2" />Settings
                    </TabsTrigger>
                    <TabsTrigger value="posts" className="flex-1 data-[state=active]:bg-verde/20 text-[#A3A3A3] data-[state=active]:text-verde text-sm py-2.5">
                      <Instagram size={14} className="mr-2" />Posts
                    </TabsTrigger>
                    <TabsTrigger value="accolades" className="flex-1 data-[state=active]:bg-verde/20 text-[#A3A3A3] data-[state=active]:text-verde text-sm py-2.5">
                      <Trophy size={14} className="mr-2" />Accolades
                    </TabsTrigger>
                    <TabsTrigger value="stats" className="flex-1 data-[state=active]:bg-verde/20 text-[#A3A3A3] data-[state=active]:text-verde text-sm py-2.5">
                      <BarChart2 size={14} className="mr-2" />Stats
                    </TabsTrigger>
                    <TabsTrigger value="security" className="flex-1 data-[state=active]:bg-verde/20 text-[#A3A3A3] data-[state=active]:text-verde text-sm py-2.5">
                      <Shield size={14} className="mr-2" />Security
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="settings">
                    <SettingsTab token={token} onSettingsUpdate={(s) => setSettings(s)} />
                  </TabsContent>
                  <TabsContent value="posts">
                    <PostsTab token={token} />
                  </TabsContent>
                  <TabsContent value="accolades">
                    <AccoladesScanTab token={token} />
                  </TabsContent>
                  <TabsContent value="stats">
                    <StatsTab token={token} />
                  </TabsContent>
                  <TabsContent value="security">
                    <SecurityTab token={token} />
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
